import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('redex_admin_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Types
export interface Client {
  id: number;
  name: string;
  email: string;
  phone?: string;
  company?: string;
  address?: string;
  notes?: string;
  status?: string;
  mrr?: number;
  created_at: string;
  updated_at: string;
}

export interface ClientWithDetails extends Client {
  sites?: Array<{
    id: number;
    name: string;
    layout: string;
    status: string;
    domain: string;
    created_at: string;
  }>;
  recentInvoices?: Array<{
    id: number;
    invoice_number: string;
    amount: number;
    status: string;
    due_date: string;
    created_at: string;
  }>;
}

export interface CreateClientData {
  name: string;
  email: string;
  phone?: string;
  company?: string;
  address?: string;
  notes?: string;
  status?: string;
  mrr?: number;
}

export interface UpdateClientData extends Partial<CreateClientData> {}

export interface ClientsResponse {
  success: boolean;
  data: {
    data: Client[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface GetClientsParams {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

// Service methods
export const clientsService = {
  // Get all clients with pagination
  async getClients(params: GetClientsParams = {}): Promise<{ data: Client[]; pagination: { total: number; page: number; limit: number; totalPages: number } }> {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.search) queryParams.append('search', params.search);
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);

    const response = await api.get<ClientsResponse>(`/clients?${queryParams.toString()}`);
    return {
      data: response.data.data.data,
      pagination: {
        total: response.data.data.total,
        page: response.data.data.page,
        limit: response.data.data.limit,
        totalPages: response.data.data.totalPages,
      },
    };
  },

  // Get single client with details
  async getClient(id: number): Promise<ClientWithDetails> {
    const response = await api.get<{ success: boolean; data: ClientWithDetails }>(`/clients/${id}`);
    return response.data.data;
  },

  // Create new client
  async createClient(data: CreateClientData): Promise<Client> {
    const response = await api.post<{ success: boolean; data: Client }>('/clients', data);
    return response.data.data;
  },

  // Update client
  async updateClient(id: number, data: UpdateClientData): Promise<Client> {
    const response = await api.put<{ success: boolean; data: Client }>(`/clients/${id}`, data);
    return response.data.data;
  },

  // Delete client
  async deleteClient(id: number): Promise<void> {
    await api.delete(`/clients/${id}`);
  },
};

export default clientsService;
